/**
 * Manage ▸ **My connections** and **Account usage** (parity card P-032).
 *
 * monday has both tabs and both are dead ends: each renders *"We've moved things to the
 * autopilot hub"* over a `Take me there` button
 * (`discovery/ui-reference/automations/05-my-connections.png`, `06-account-usage.png`). This
 * product has no autopilot hub, so copying the dead end would be copying a redirect to
 * nowhere. The tabs exist — which is what the parity shot measures — and each shows what this
 * product actually knows, from data the centre has already loaded:
 *
 *  - **My connections** — the account's integration connections, the same
 *    `pickers.connections` map the integration slots read (M6-A4); connecting one is an admin
 *    action, on Admin ▸ Connections, so this is a read-out and says so;
 *  - **Account usage** — the run counters the engine already keeps on each automation's
 *    `stats`, totalled and then listed per automation.
 */
import { INTEGRATION_APPS, type IntegrationApp } from '@elizmonday/shared';
import type { AutomationSummaryView, PickerDataView } from './types';

const APP_LABELS: Record<IntegrationApp, string> = {
  slack: 'Slack',
  gmail: 'Gmail',
  googleCalendar: 'Google Calendar',
  webhooks: 'Webhooks',
};

export function ConnectionsPanel({ pickers }: { pickers: PickerDataView | null }) {
  const connections = pickers?.connections ?? {};
  return (
    <div className="ez-automation-manage__panel" data-testid="automations-connections-panel">
      <p className="ez-automation-panel__lede">
        Integration connections belong to the account, not to this board. An admin connects and
        revokes them on <strong>Admin ▸ Connections</strong>; an automation whose app is not
        connected parks itself rather than failing.
      </p>
      <ul className="ez-automation-connections" data-testid="automations-connections">
        {INTEGRATION_APPS.map((app) => {
          const connected = connections[app] === 'connected';
          return (
            <li key={app} className="ez-automation-connection" data-testid={`automations-connection-${app}`}>
              <span className="ez-automation-connection__name">{APP_LABELS[app]}</span>
              <span className="ez-automation-connection__state" data-connected={connected}>
                {connected ? 'Connected' : 'Not connected'}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** `stats` is free-form json on the row; only the two counters the engine writes are read. */
function counter(row: AutomationSummaryView, key: 'runs' | 'failed'): number {
  const stats = (row.stats ?? {}) as Record<string, unknown>;
  const value = stats[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

export function UsagePanel({ rows }: { rows: readonly AutomationSummaryView[] }) {
  const totals = rows.reduce(
    (acc, row) => ({
      runs: acc.runs + counter(row, 'runs'),
      failed: acc.failed + counter(row, 'failed'),
      active: acc.active + (row.runnable ? 1 : 0),
    }),
    { runs: 0, failed: 0, active: 0 },
  );
  const used = rows.filter((row) => counter(row, 'runs') > 0);

  return (
    <div className="ez-automation-manage__panel" data-testid="automations-usage-panel">
      <p className="ez-automation-panel__lede">
        This board&apos;s automation usage. Every run the engine records is counted here — there
        is no action quota on a self-hosted account, so these are counters, not a budget.
      </p>
      <div className="ez-automation-usage" data-testid="automations-usage">
        <UsageTile label="Automations" value={rows.length} testId="usage-total" />
        <UsageTile label="Switched on" value={totals.active} testId="usage-active" />
        <UsageTile label="Runs" value={totals.runs} testId="usage-runs" />
        <UsageTile label="Failed runs" value={totals.failed} testId="usage-failed" />
      </div>
      {used.length === 0 ? (
        <p className="ez-automation-panel__lede" data-testid="automations-usage-empty">
          No automation on this board has run yet.
        </p>
      ) : (
        <ul className="ez-automation-usage__rows">
          {used.map((row) => (
            <li key={row.id} className="ez-automation-usage__row" data-testid={`automations-usage-${row.id}`}>
              <span className="ez-automation-usage__title">{row.title}</span>
              <span className="ez-automation-usage__count">{counter(row, 'runs')} runs</span>
              {counter(row, 'failed') > 0 ? (
                <span className="ez-automation-usage__failed">{counter(row, 'failed')} failed</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function UsageTile({ label, value, testId }: { label: string; value: number; testId: string }) {
  return (
    <div className="ez-automation-usage__tile" data-testid={testId}>
      <span className="ez-automation-usage__value">{value}</span>
      <span className="ez-automation-usage__label">{label}</span>
    </div>
  );
}
